import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { invalidateCachedFetch, useAuth } from '@structural-climbing/auth'
import { Button, Card, CardBody, Spinner } from '@structural-climbing/ui'
import { MY_MEMBERSHIPS_KEY } from '../features/gym/useMyMemberships'
import { forgetChildInvite, rememberChildInvite } from '../features/family/pendingChildInvite'

type State =
  | { kind: 'loading' }
  /** Signed out, invite is good: say who it is for and offer to sign up. */
  | { kind: 'invite'; firstName: string; email: string }
  | { kind: 'gone' }
  | { kind: 'wrongEmail' }
  | { kind: 'inUse' }
  | { kind: 'error' }

/**
 * Where a child's invite link lands.
 *
 * Signed out, it names the child and sends them to sign up. Signed in, it
 * claims the invite and hands over to the app, which by then knows this login
 * is a child's and shows the climbing screens only.
 *
 * Outside RequireRole on purpose: the reader has no account yet.
 */
export function ChildInvitePage() {
  const { isAuthenticated, loginWithRedirect, logout, getAccessTokenSilently } = useAuth()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const token = params.get('token')

  const [state, setState] = useState<State>({ kind: 'loading' })
  // The accept call is not something to fire twice because an effect re-ran.
  const accepting = useRef(false)

  const accept = useCallback(async () => {
    if (!token || accepting.current) return
    accepting.current = true
    setState({ kind: 'loading' })
    try {
      const accessToken = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/child-invites/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      if (res.ok) {
        forgetChildInvite()
        // The gym list was loaded, empty, before the link existed.
        invalidateCachedFetch(MY_MEMBERSHIPS_KEY)
        void navigate('/', { replace: true })
        return
      }
      const data = (await res.json().catch(() => ({}))) as { code?: string }
      if (data.code === 'WRONG_EMAIL') setState({ kind: 'wrongEmail' })
      else if (data.code === 'ACCOUNT_IN_USE') setState({ kind: 'inUse' })
      else if (res.status === 404) {
        forgetChildInvite()
        setState({ kind: 'gone' })
      } else setState({ kind: 'error' })
    } catch {
      setState({ kind: 'error' })
    } finally {
      accepting.current = false
    }
  }, [token, apiUrl, getAccessTokenSilently, navigate])

  useEffect(() => {
    if (!token) {
      setState({ kind: 'gone' })
      return
    }
    if (isAuthenticated) {
      void accept()
      return
    }
    let cancelled = false
    fetch(`${apiUrl}/child-invites/${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (cancelled) return
        if (!res.ok) {
          if (res.status === 404) forgetChildInvite()
          setState({ kind: res.status === 404 ? 'gone' : 'error' })
          return
        }
        const data = (await res.json()) as { first_name: string; email: string }
        // Kept for the trip through sign-up, in case the child comes back to
        // the app some other way than the redirect this page starts.
        rememberChildInvite(token)
        setState({ kind: 'invite', firstName: data.first_name, email: data.email })
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [token, isAuthenticated, apiUrl, accept])

  function signUp(email: string) {
    // The token rides in the URL, and appState.returnTo is how it survives the
    // trip through Auth0. sessionStorage is the fallback for a browser that
    // drops appState, the same pair the join page relies on.
    const returnTo = `${window.location.pathname}${window.location.search}`
    sessionStorage.setItem('auth_return_to', returnTo)
    void loginWithRedirect({
      appState: { returnTo },
      authorizationParams: { screen_hint: 'signup', login_hint: email },
    })
  }

  // Signing out lands on the app's front door, without the token. Remembered
  // here so the account they sign up with next is taken back to the invite.
  const signOut = () => {
    if (token) rememberChildInvite(token)
    void logout({ logoutParams: { returnTo: window.location.origin } })
  }

  if (state.kind === 'loading') {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3">
        <Spinner size="lg" color="primary" />
        {isAuthenticated && (
          <p className="text-sm text-foreground/50">{t('childInvite.accepting')}</p>
        )}
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-md p-6">
      <Card>
        <CardBody className="flex flex-col gap-4 py-6 text-center">
          {state.kind === 'invite' ? (
            <>
              <div>
                <h1 className="text-lg font-semibold">
                  {t('childInvite.title', { name: state.firstName })}
                </h1>
                <p className="mt-1 text-sm text-foreground/60">{t('childInvite.intro')}</p>
              </div>
              <p className="break-words text-xs text-foreground/50">
                {t('childInvite.signUpWith', { email: state.email })}
              </p>
              <Button color="primary" className="w-full" onClick={() => signUp(state.email)}>
                {t('childInvite.button')}
              </Button>
            </>
          ) : (
            <>
              <h1 className="text-lg font-semibold">{t('childInvite.titleFallback')}</h1>
              <p className="text-sm text-foreground/60">
                {t(
                  state.kind === 'gone'
                    ? 'childInvite.gone'
                    : state.kind === 'wrongEmail'
                      ? 'childInvite.wrongEmail'
                      : state.kind === 'inUse'
                        ? 'childInvite.inUse'
                        : 'childInvite.error',
                )}
              </p>
              {(state.kind === 'wrongEmail' || state.kind === 'inUse') && (
                <Button variant="flat" className="w-full" onClick={signOut}>
                  {t('common.signOut')}
                </Button>
              )}
              {state.kind === 'error' && (
                <Button
                  variant="flat"
                  className="w-full"
                  onClick={() => (isAuthenticated ? void accept() : window.location.reload())}
                >
                  {t('auth.tryAgain')}
                </Button>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  )
}
